import { createFileRoute } from "@tanstack/react-router";
import { Clause, LegalLayout } from "@/components/sox/LegalLayout";
import { LEGAL, LEGAL_ADDRESS_LINE } from "@/lib/legal";

export const Route = createFileRoute("/returns")({
  head: () => ({
    meta: [
      { title: "Returns & Refunds — SOXIE365" },
      {
        name: "description",
        content:
          "SOXIE365 returns policy: 14-day EU right of withdrawal, our 30-day return window, refund timings and the model withdrawal form.",
      },
      { property: "og:title", content: "Returns & Refunds — SOXIE365" },
      {
        property: "og:description",
        content: "How to cancel, return or get refunded on a SOXIE365 order.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://www.soxie365.com/returns" },
    ],
    links: [{ rel: "canonical", href: "https://www.soxie365.com/returns" }],
  }),
  component: ReturnsPage,
});

function ReturnsPage() {
  return (
    <LegalLayout
      index="04"
      title="Returns & Refunds"
      intro="Statutory rights first, then our own policy on top. Both are written out below so there are no surprises."
    >
      <Clause heading="1. Right of withdrawal (EU)">
        <p>
          You may withdraw from your purchase within <strong>14 days</strong> of the day you (or a
          person you nominate) take physical possession of the goods, without giving any reason. To
          exercise it, send a clear statement to{" "}
          <a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a> before the 14 days
          expire — an email is enough; you may use the form in section 6.
        </p>
        <p>
          After withdrawing, return the goods without undue delay and within 14 days. We refund all
          payments received, including the standard delivery cost, within 14 days of receiving the
          goods back or proof that you sent them, using the same payment method you used, at no fee
          to you.
        </p>
      </Clause>

      <Clause heading="2. Our 30-day return window">
        <p>
          Beyond the statutory period, we accept returns of <strong>unopened boxes for 30 days</strong>{" "}
          from delivery for a full refund. The outer box must be intact with its seal unbroken.
        </p>
      </Clause>

      <Clause heading="3. Opened boxes">
        <p>
          Opened the box and it's not for you? Contact us within 7 days of delivery and we will
          refund you — you do not need to ship 365 pairs back. We may ask for photos. This goodwill
          guarantee is one per customer and does not affect your statutory rights.
        </p>
        <p>
          For hygiene reasons, individually sealed pairs that have been opened cannot be resold; a
          proportionate deduction may apply where value has been diminished by handling beyond what
          is needed to inspect the goods.
        </p>
      </Clause>

      <Clause heading="4. Faulty, damaged or wrong items">
        <p>
          If a box arrives damaged, incomplete, or is not what you ordered, email us within a
          reasonable time with your order reference and a photo. We pay return shipping and you can
          choose repair, replacement, or a refund in line with the two-year EU legal guarantee (or
          your equivalent local right).
        </p>
      </Clause>

      <Clause heading="5. Return costs and address">
        <p>
          Unless the item is faulty, damaged in transit, or wrongly sent, you pay the direct cost of
          returning the goods. Do not ship anything back before contacting us — we issue a return
          reference and confirm the correct return address for your region. Registered address:{" "}
          {LEGAL_ADDRESS_LINE}.
        </p>
      </Clause>

      <Clause heading="6. Model withdrawal form">
        <div className="border-2 border-black bg-black/[0.03] p-6 font-mono text-xs leading-relaxed">
          <p>To {LEGAL.entity}, {LEGAL_ADDRESS_LINE} — {LEGAL.supportEmail}</p>
          <p className="mt-4">
            I/We hereby give notice that I/We withdraw from my/our contract of sale of the following
            goods:
          </p>
          <p className="mt-4">Ordered on: ____________ Received on: ____________</p>
          <p className="mt-4">Order reference: ____________</p>
          <p className="mt-4">Name of consumer: ____________</p>
          <p className="mt-4">Address of consumer: ____________</p>
          <p className="mt-4">Signature (paper only): ____________ Date: ____________</p>
        </div>
      </Clause>

      <Clause heading="7. Refund timing">
        <p>
          Refunds are issued to the original payment method via Stripe. We process them within 14
          days of approval; your bank may take a further 5–10 business days to show the credit.
        </p>
      </Clause>
    </LegalLayout>
  );
}
